import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { processDroppedDataTransfer } from '@/lib/dragDropUtils';

interface UseFolderDropZoneProps {
  folderId: number;
  folderName: string;
  onFilesDropped: (files: File[], folderId: number) => void;
  disabled?: boolean;
  onDragHover?: () => void;
  autoExpandDelay?: number;
}

export function useFolderDropZone({
  folderId,
  folderName,
  onFilesDropped,
  disabled = false,
  onDragHover,
  autoExpandDelay = 400
}: UseFolderDropZoneProps) {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const dragCounterRef = useRef(0);
  const expandTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (expandTimerRef.current) {
        clearTimeout(expandTimerRef.current);
      }
    };
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) return;

    // Check if dragging files
    if (e.dataTransfer.types.includes('Files')) {
      dragCounterRef.current++;
      setIsDraggingOver(true);

      // Start auto-expand timer on first drag enter
      if (dragCounterRef.current === 1 && onDragHover && !expandTimerRef.current) {
        expandTimerRef.current = setTimeout(() => {
          onDragHover();
          expandTimerRef.current = null;
        }, autoExpandDelay);
      }
    }
  }, [disabled, onDragHover, autoExpandDelay]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) return;

    if (e.dataTransfer.types.includes('Files')) {
      e.dataTransfer.dropEffect = 'copy';
    }
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (disabled) return;

    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDraggingOver(false);

      // Clear auto-expand timer when drag leaves
      if (expandTimerRef.current) {
        clearTimeout(expandTimerRef.current);
        expandTimerRef.current = null;
      }
    }
  }, [disabled]);

  const handleDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    dragCounterRef.current = 0;
    setIsDraggingOver(false);

    // Clear auto-expand timer on drop
    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
      expandTimerRef.current = null;
    }

    if (disabled) return;

    try {
      const files = await processDroppedDataTransfer(e.dataTransfer);
      if (files.length > 0) {
        onFilesDropped(files, folderId);
      } else {
        toast.error('No files could be read from the drop. Try selecting them using the upload button.');
      }
    } catch (error) {
      console.error('Error processing dropped folder:', error);
      toast.error('Failed to read the dropped files. Please try again.');
    }
  }, [disabled, folderId, onFilesDropped]);

  return {
    isDraggingOver,
    dragHandlers: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
}
